import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db, call } from "../firebase";
import { useSession } from "../session";
import { SITES, DOC_CATEGORIES, SENSITIVE, isGlobal, canUploadAny } from "../domain";
import { friendly } from "../errors";

interface StartOut { docId: string; code: string | null; version: number; name: string; storage: "drive" | "r2"; upload: { method: "PUT"; url: string; headers: Record<string, string> } }
interface DoneOut { code: string; name: string; version: number }

class Offline extends Error {}

/** PUT có báo tiến độ; mất mạng thì hủy ngay thay vì để thanh tiến độ chạy giả tới 100% (L15). */
function put(url: string, file: File, headers: Record<string, string>, onProgress: (p: number) => void): { done: Promise<string>; abort: () => void } {
  const x = new XMLHttpRequest();
  const done = new Promise<string>((resolve, reject) => {
    const off = () => { x.abort(); reject(new Offline()); };
    window.addEventListener("offline", off, { once: true });
    const cleanup = () => window.removeEventListener("offline", off);
    x.open("PUT", url);
    Object.entries(headers).forEach(([k, v]) => x.setRequestHeader(k, v));
    x.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    x.onload = () => { cleanup(); x.status >= 200 && x.status < 300 ? resolve(x.responseText) : reject(new Error(`upload ${x.status}`)); };
    x.onerror = () => { cleanup(); reject(navigator.onLine ? new Error("network") : new Offline()); };
    x.onabort = () => cleanup();
    x.send(file);
  });
  return { done, abort: () => x.abort() };
}

const MAX_FILES = 30;
const size = (b: number) => (b < 1048576 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1048576).toFixed(1)} MB`);
/** "HD_thue-dat ATV 2026.pdf" → "HD thue dat ATV 2026": tiêu đề gợi ý từ tên file. */
const titleFromName = (n: string) => n.replace(/\.[^.]+$/, "").replace(/[_\-.]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 150);

type ItemState = "wait" | "up" | "done" | "err";
interface Item { key: string; file: File; title: string; state: ItemState; pct: number; out?: DoneOut; err?: string }

export default function Upload() {
  const { member } = useSession();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const replace = params.get("replace");
  const [orig, setOrig] = useState<{ code: string; status: string } | null>(null);
  const [f, setF] = useState({ siteId: "", category: "", subcategory: "", title: "", ownerName: member?.name ?? "", language: "vi", expiresAt: "", tags: "" });
  const [items, setItems] = useState<Item[]>([]);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const patch = (key: string, p: Partial<Item>) => setItems((xs) => xs.map((x) => (x.key === key ? { ...x, ...p } : x)));

  // Bản mới / gửi bản sửa: khóa cơ sở + loại theo tài liệu gốc
  useEffect(() => {
    if (!replace) return;
    getDoc(doc(db, "documents", replace)).then((s) => {
      const d = s.data(); if (!d) return;
      setOrig({ code: d.code, status: d.status });
      setF((x) => ({ ...x, siteId: d.siteId, category: d.category, subcategory: d.subcategory, title: d.title, ownerName: d.ownerName }));
    }).catch((e) => setErr(friendly(e)));
  }, [replace]);

  if (!member) return null;
  if (!canUploadAny(member)) return <><h1>Tải lên tài liệu</h1><div className="card">Vai trò của bạn chỉ được xem tài liệu, không tải lên.</div></>;

  // Cơ sở được tải lên: Quản lý/Nhân viên chỉ cơ sở mình (không gồm "Toàn công ty")
  const siteOptions = isGlobal(member) ? Object.keys(SITES) : member.sites;
  const catOptions = Object.keys(DOC_CATEGORIES).filter((c) => member.role !== "staff" || !SENSITIVE.includes(c));
  // Quyền vừa đổi (L5): bỏ lựa chọn không còn hợp lệ
  const siteId = replace || siteOptions.includes(f.siteId) ? f.siteId : "";
  const category = replace || catOptions.includes(f.category) ? f.category : "";
  const multi = items.length > 1;
  const titlesOk = multi ? items.every((i) => i.title.trim()) : !!f.title.trim();
  const ready = siteId && category && f.subcategory.trim() && f.ownerName.trim() && items.length > 0 && titlesOk;
  const busy = running;
  const isFix = orig?.status === "changes_requested";
  const heading = !replace ? "Tải lên tài liệu" : isFix ? `Gửi bản sửa · ${orig?.code ?? ""}` : `Tải bản mới · ${orig?.code ?? ""}`;

  const pick = (list: FileList | null) => {
    setErr(null);
    const arr = Array.from(list ?? []);
    if (arr.length > MAX_FILES) setErr(`Mỗi lần tải tối đa ${MAX_FILES} file. Đã lấy ${MAX_FILES} file đầu.`);
    const next = arr.slice(0, MAX_FILES).map((file, i): Item => ({ key: `${Date.now()}-${i}-${file.name}`, file, title: titleFromName(file.name), state: "wait", pct: 0 }));
    setItems(next);
    // Một file, chưa có tiêu đề → gợi ý tiêu đề từ tên file
    if (next.length === 1 && !replace && !f.title.trim()) setF((x) => ({ ...x, title: next[0].title }));
  };

  /** Tải một file thành một tài liệu: tạo hồ sơ → PUT → hoàn tất (cấp mã). Lỗi thì hủy lượt dở. */
  const uploadOne = async (it: Item, title: string): Promise<DoneOut> => {
    let started: StartOut | null = null;
    try {
      started = await call<object, StartOut>("createDocumentUpload")({
        ...f, title, siteId, category, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean), expiresAt: f.expiresAt || null,
        supersedesDocId: replace, fileName: it.file.name, mime: it.file.type || "application/octet-stream", size: it.file.size,
      });
      patch(it.key, { state: "up" });
      const body = await put(started.upload.url, it.file, started.upload.headers, (p) => patch(it.key, { pct: p })).done;
      patch(it.key, { pct: 1 });
      const driveFileId = started.storage === "drive" ? (JSON.parse(body) as { id: string }).id : undefined;
      return await call<object, DoneOut>("finalizeDocumentUpload")({ docId: started.docId, version: started.version, driveFileId });
    } catch (e) {
      if (started) {
        const s = started;
        // L37, L4: ghi "Tải lên lỗi" và bỏ lượt dở. Mạng vừa có lại thường chưa ổn ngay → thử lại vài lần.
        const cancel = async () => {
          for (const ms of [800, 3000, 8000, 20000]) {
            await new Promise((r) => setTimeout(r, ms));
            if (!navigator.onLine) continue;
            try { await call("cancelDocumentUpload")({ docId: s.docId, reason: "failed" }); return; } catch { /* thử lại */ }
          }
        };
        if (navigator.onLine) void cancel(); else window.addEventListener("online", () => void cancel(), { once: true });
      }
      throw e;
    }
  };

  /** Tải lần lượt từng file (mã cấp theo đúng thứ tự danh sách). Chỉ chạy các file chưa xong → bấm lại để thử lại file lỗi. */
  const submit = async () => {
    setErr(null); setRunning(true);
    const queue = items.filter((i) => i.state !== "done");
    for (const it of queue) {
      if (!navigator.onLine) { patch(it.key, { state: "err", err: "Mất kết nối mạng." }); continue; }
      patch(it.key, { state: "up", pct: 0, err: undefined });
      try {
        const out = await uploadOne(it, multi ? it.title.trim() : f.title.trim());
        patch(it.key, { state: "done", out });
      } catch (e) {
        patch(it.key, { state: "err", err: e instanceof Offline ? "Mất kết nối mạng, tải lên chưa xong." : friendly(e, "Tải lên chưa xong.") });
      }
    }
    setRunning(false);
    setFinished(true);
  };

  const reset = () => { setItems([]); setFinished(false); setErr(null); if (fileRef.current) fileRef.current.value = ""; };
  const ok = items.filter((i) => i.state === "done");
  const failed = items.filter((i) => i.state === "err");

  // Xong hết → màn hình kết quả
  if (finished && !running && failed.length === 0 && ok.length > 0) return (
    <>
      <h1>{replace ? "Đã gửi bản mới" : ok.length > 1 ? `Đã tải lên ${ok.length} tài liệu` : "Đã tải lên"}</h1>
      <div className="card" style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 720 }}>
        <div className="ok">Hub đã cấp mã và gửi duyệt.</div>
        {ok.length === 1 ? (<>
          <div><span className="muted">Mã tài liệu</span><div className="code" style={{ fontSize: 22 }}>{ok[0].out!.code}</div></div>
          <div><span className="muted">Tên file chuẩn · phiên bản v{ok[0].out!.version}</span><div style={{ wordBreak: "break-all" }}>{ok[0].out!.name}</div></div>
        </>) : (
          <ul className="upList">{ok.map((i) => <li key={i.key}><span className="code">{i.out!.code}</span><span className="upName">{i.title}</span></li>)}</ul>
        )}
        <div className="row">
          {!replace && <button className="btn primary" onClick={reset}>Tải tiếp tài liệu khác</button>}
          <Link className="btn" style={{ display: "flex", alignItems: "center", textDecoration: "none" }} to="/documents">Về Kho tài liệu</Link>
        </div>
      </div>
    </>
  );

  return (
    <>
      <h1>{heading}</h1>
      <p className="sub">Hub tự cấp mã khi tải lên xong, đặt tên file và xếp vào đúng thư mục. Ảnh, video, file thiết kế tự vào kho media.{!replace && " Chọn được nhiều file một lần: mỗi file thành một tài liệu riêng, dùng chung các thông tin bên dưới."}</p>
      <div className="card" style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 900 }}>
        <div className="field" style={{ flex: "none" }}>
          <label htmlFor="file">{replace ? "File *" : `File * (tối đa ${MAX_FILES} file)`}</label>
          <input id="file" ref={fileRef} type="file" multiple={!replace} disabled={busy || finished} onChange={(e) => pick(e.target.files)} />
        </div>
        <div className="grid">
          <div className="field"><label htmlFor="site">Cơ sở *</label>
            <select id="site" value={siteId} onChange={set("siteId")} disabled={!!replace || busy || finished}><option value="">Chọn…</option>
              {(replace ? [f.siteId] : siteOptions).filter(Boolean).map((k) => <option key={k} value={k}>{SITES[k]}</option>)}</select></div>
          <div className="field"><label htmlFor="cat">Loại tài liệu *</label>
            <select id="cat" value={category} onChange={set("category")} disabled={!!replace || busy || finished}><option value="">Chọn…</option>
              {(replace ? [f.category] : catOptions).filter(Boolean).map((k) => <option key={k} value={k}>{DOC_CATEGORIES[k]}</option>)}</select></div>
          <div className="field"><label htmlFor="sub">Tiểu loại *</label><input id="sub" value={f.subcategory} disabled={busy || finished} onChange={set("subcategory")} placeholder="vd: Profile, Hợp đồng thuê" /></div>
          {!multi && <div className="field"><label htmlFor="title">Tiêu đề *</label><input id="title" value={f.title} disabled={busy || finished || !!replace} onChange={set("title")} /></div>}
          <div className="field"><label htmlFor="owner">Người phụ trách *</label><input id="owner" value={f.ownerName} disabled={busy || finished} onChange={set("ownerName")} /></div>
          <div className="field"><label htmlFor="lang">Ngôn ngữ</label>
            <select id="lang" value={f.language} disabled={busy || finished} onChange={set("language")}><option value="vi">Tiếng Việt</option><option value="en">English</option><option value="vi-en">Tiếng Việt + English</option></select></div>
          <div className="field"><label htmlFor="exp">Hiệu lực đến (HĐ, giấy phép, bảo hiểm)</label><input id="exp" type="date" value={f.expiresAt} disabled={busy || finished} onChange={set("expiresAt")} /></div>
          <div className="field"><label htmlFor="tags">Nhãn (cách nhau bằng dấu phẩy)</label><input id="tags" value={f.tags} disabled={busy || finished} onChange={set("tags")} /></div>
        </div>

        {multi && (
          <div>
            <div className="row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
              <b>{items.length} file · sửa tiêu đề từng tài liệu nếu cần</b>
              {running && <span className="muted">Đang tải {ok.length + failed.length + 1}/{items.length}…</span>}
            </div>
            <ul className="upList">
              {items.map((i) => (
                <li key={i.key} className={`up-${i.state}`}>
                  <span className="upIcon" aria-hidden="true">{i.state === "done" ? "✓" : i.state === "err" ? "!" : i.state === "up" ? "↑" : "·"}</span>
                  <div className="upBody">
                    <input aria-label={`Tiêu đề cho ${i.file.name}`} value={i.title} disabled={busy || i.state === "done"} onChange={(e) => patch(i.key, { title: e.target.value })} />
                    <div className="muted upFile">{i.file.name} · {size(i.file.size)}
                      {i.state === "done" && i.out && <> · <span className="code">{i.out.code}</span></>}
                      {i.state === "err" && <span className="upErr"> · {i.err}</span>}</div>
                    {i.state === "up" && <div className="progress thin"><div style={{ width: `${Math.round(i.pct * 100)}%` }} /></div>}
                  </div>
                  {!busy && i.state !== "done" && <button className="btn ghost" aria-label={`Bỏ ${i.file.name}`} onClick={() => setItems((xs) => xs.filter((x) => x.key !== i.key))}>✕</button>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!multi && running && items[0] && (
          <div>
            <div className="progress" aria-label="Tiến độ tải lên"><div style={{ width: `${Math.round(items[0].pct * 100)}%` }} /></div>
            <div className="muted" style={{ marginTop: 6 }}>{items[0].pct < 1 ? `Đang tải lên… ${Math.round(items[0].pct * 100)}%` : "Đã gửi xong file, Hub đang kiểm tra và cấp mã…"}</div>
          </div>
        )}
        {!multi && items[0]?.state === "err" && <div className="err">{items[0].err} Bấm "{replace ? (isFix ? "Gửi bản sửa" : "Tải bản mới") : "Tải lên"}" để thử lại.</div>}
        {multi && finished && failed.length > 0 && <div className="err">{ok.length} file đã xong, {failed.length} file chưa tải được. Bấm "Thử lại file lỗi" hoặc bỏ các file đó.</div>}
        {err && <div className="err">{err}</div>}
        {!ready && !busy && !finished && <div className="muted">Cần chọn file và điền đủ các ô có dấu *.</div>}
        <div className="row" style={{ justifyContent: "flex-end" }}>
          <button className="btn" disabled={busy} onClick={() => (finished && ok.length ? reset() : nav("/documents"))}>{finished && ok.length ? "Làm lại từ đầu" : "Hủy"}</button>
          <button className="btn primary" disabled={!ready || busy || (finished && failed.length === 0)} onClick={submit}>
            {finished && failed.length ? "Thử lại file lỗi" : replace ? (isFix ? "Gửi bản sửa" : "Tải bản mới") : multi ? `Tải lên ${items.length} file & gửi duyệt` : "Tải lên & gửi duyệt"}
          </button>
        </div>
      </div>
    </>
  );
}
